import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Form, Row } from "antd";
import dayjs from "dayjs";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import entityType from "../../api/entityType";
import {
  exportReport,
  fetchReport,
  resetReportState,
} from "../../store/report/reportActions";
import { transformBcarRetentionInput } from "../../store/retention/retentionActions";
import PermissionWrapper from "../navigation/PermissionWrapper";
import ReportPanel from "../shared/ReportPanel";
import AgencyMultiSelectEx from "../shared/form/AgencyMultiSelectEx";
import DrillDownSortFieldSelectEx from "../shared/form/DrillDownSortFieldSelectEx";
import ExtraReportFilterContainer from "../shared/form/ExtraReportFiltersContainer";
import ProducerSelectEx from "../shared/form/ProducerSelectEx";
import {
  dateRangeConfigs,
  fixedDateRanges,
} from "../shared/form/fixedDateRanges";
import { getInitialAgency } from "../shared/form/getInitialAgency";
import AdvancedRangePicker from "../shared/form/reactHookForm/AdvancedRangePicker";
import CustomCheckbox from "../shared/form/reactHookForm/CustomCheckbox";
import { dateFormat, toEndOfDay, toStartOfDay } from "../shared/formatting";
import { ViewRetentionData } from "../shared/securityRoleTasks";
import { agenciesListEqual } from "../shared/sorting";
import { basicRetentionReportFieldOptions } from "./reportParameters";
import { bcarRetentionReportSchema } from "./reportSchema";

// Constants
const partialInitialValues = {
  AgencyKeys: [],
  FixedDateRange: fixedDateRanges.LAST_MONTH,
  ExpiryDate: ["", ""],
  IncludeLateRenewals: false,
  DrillDownSortFields: [],
  ProducerCode1Keys: [],
  ProducerCode2Keys: [],
};

const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 19 },
};

const reportName = "BCautorenewRetentionReport";

const BcarRetentionReport = () => {
  // Form
  const {
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { isValid, isDirty, defaultValues, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: partialInitialValues,
    resolver: yupResolver(bcarRetentionReportSchema),
  });

  const selectedDateRange = watch("ExpiryDate");

  //
  const dispatch = useDispatch();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [initialDefaultValues, setInitialDefaultValues] =
    useState(partialInitialValues);

  //
  const agencies = useSelector((state) => state.agency.listCompact);
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);

  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      var initialAgency = getInitialAgency(homeAgency, agencies);
      const fullInitialValues = {
        ...partialInitialValues,
        AgencyKeys: [initialAgency],
        ExpiryDate: [
          toStartOfDay(dayjs().subtract(1, "month").startOf("month")),
          toEndOfDay(dayjs().subtract(1, "month").endOf("month")),
        ],
      };

      setInitialDefaultValues(cloneDeep(fullInitialValues));
      reset(fullInitialValues);
      trigger();
    }
  }, [agencies, homeAgency]);

  const onSubmitHandler = (values) => {
    dispatch(
      fetchReport(
        transformBcarRetentionInput(values, ViewRetentionData),
        entityType.RETENTION,
        `PrintRetention`,
        () => {
          reset(values);
        }
      )
    );
  };

  const handlers = {
    onSubmit: () => handleSubmit(onSubmitHandler)(),
    onExport: () =>
      dispatch(
        exportReport(
          transformBcarRetentionInput(getValues(), ViewRetentionData),
          reportName,
          entityType.RETENTION
        )
      ),
    onRestore: () => {
      reset(initialDefaultValues);
      dispatch(resetReportState());
    },
  };

  const buttons = [
    {
      action: "preview",
      disabled: !isValid,
    },
    {
      action: "export",
      disabled: !isValid,
      onClick: handlers.onExport,
    },
    { action: "restore", onClick: handlers.onRestore },
  ];

  return (
    <PermissionWrapper entityName="RetentionReport">
      <ReportPanel
        isDirty={isDirty}
        isSubmitted={isSubmitted}
        onSubmit={handlers.onSubmit}
        buttons={buttons}
        taskId={ViewRetentionData}
      >
        <Row>
          <Col span={10}>
            <AgencyMultiSelectEx
              layout={layout}
              formLabel="Agency"
              required={true}
              agencies={currentAgencies}
              isLoading={isEmpty(defaultValues.AgencyKeys)}
              setValue={setValue}
              control={control}
              name="AgencyKeys"
            />
          </Col>
          <Col span={14}>
            <AdvancedRangePicker
              formItem={{
                layout: layout,
                required: true,
                label: "Date",
              }}
              control={control}
              setValue={setValue}
              allowClear={true}
              name="ExpiryDate"
              format={dateFormat}
              dateRangeConfig={dateRangeConfigs.NON_TRANSACTION_NO_FUTURE}
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              } // Should we allow users to select over a year?
            />
          </Col>
        </Row>
        <Row>
          <Col span={10}>
            <CustomCheckbox
              control={control}
              layout={layout}
              formLabel="Include"
              name="IncludeLateRenewals"
              formTooltip="In order to include late renewals, the ending date must be at least 30 days prior to the current date."
              disabled={
                dayjs().endOf("day").diff(dayjs(selectedDateRange[1]), "day") <
                30
              }
            >
              Late Renewals (up to 30 days)
            </CustomCheckbox>
          </Col>
        </Row>
        <ExtraReportFilterContainer>
          <Row>
            <Col span={12}>
              <Form.Item
                name="ProducerCode1Keys"
                label="Producer 1"
                {...layout}
              >
                <ProducerSelectEx
                  name="ProducerCode1Keys"
                  setValue={setValue}
                  control={control}
                  producerNumber={1}
                  taskKey={ViewRetentionData}
                />
              </Form.Item>
              <Form.Item
                name="ProducerCode2Keys"
                label="Producer 2"
                {...layout}
              >
                <ProducerSelectEx
                  name="ProducerCode2Keys"
                  setValue={setValue}
                  control={control}
                  producerNumber={2}
                  taskKey={ViewRetentionData}
                />
              </Form.Item>
            </Col>
          </Row>
        </ExtraReportFilterContainer>

        <Row>
          <Col span={24}>
            <DrillDownSortFieldSelectEx
              fieldOptions={basicRetentionReportFieldOptions}
              control={control}
              trigger={trigger}
              isSubmitted={isSubmitted}
            />
          </Col>
        </Row>
      </ReportPanel>
    </PermissionWrapper>
  );
};
export default BcarRetentionReport;
